import React, { useCallback, useEffect, useRef } from 'react';
import { ToastVariants } from '@dotcom/crown-ui';
import { EnvelopePreview } from '../../components/envelope-preview/envelope-preview';
import { Layout } from '../../components/layout';
import { useAnalyticsContext } from '../../context/analytics-context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen, showLoadingScreen, useAppContext } from '../../context/app-context';
import { useInitializationDataContext } from '../../context/data-context/data-context';
import { useLoadExistingProject } from '../editor/hooks/use-load-existing-project';
import { SelectEnvelopeCard } from './components/select-envelope-card';
import { useEnvelopeSelection } from './hooks/use-envelope-selection';
import styles from './select-envelope.module.scss';

export const SelectEnvelopeView = () => {
  const {
    envelopeForm: { register, setValue },
    selectedEnvelope,
    handleBack,
    handleContinue,
  } = useEnvelopeSelection();
  const { trackEnvelopePrintingOptionPage, trackEnvelopeOptionChosen } = useAnalyticsContext();
  const { appDispatch } = useAppContext();
  const hasTracked = useRef(false);
  const { loadExistingProject } = useLoadExistingProject();
  const lastProjectLoaded = sessionStorage.getItem('lastProjectLoaded');
  const isDataLoaded = useRef<boolean>(false);
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const loadProject = useCallback(async () => {
    showLoadingScreen(appDispatch, '');
    await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
    hideLoadingScreen(appDispatch);
  }, [appDispatch, lastProjectLoaded, loadExistingProject]);

  // TO DO - Move this logic to a more global place so that it is not needed
  // to be called in every view that needs initialized data
  useEffect(() => {
    if (initializedData === null) {
      loadProject();
    }
  }, [initializedData]);

  useEffect(() => {
    if (!hasTracked.current) {
      trackEnvelopePrintingOptionPage();
      hasTracked.current = true;
    }
  }, []);

  useEffect(() => {
    if (selectedEnvelope) {
      setValue('envelopeOption', selectedEnvelope);
    }
  }, []);

  const onContinue = () => {
    showLoadingScreen(appDispatch, '');
    handleContinue()
      ?.then(() => {
        hideLoadingScreen(appDispatch);
        if (selectedEnvelope) {
          trackEnvelopeOptionChosen(selectedEnvelope);
        }
      })
      .catch(() => {
        hideLoadingScreen(appDispatch);
        setIsToasterOpen(appDispatch, {
          variant: ToastVariants.Error,
          title: 'Error',
          children: 'An error occurred saving your envelope selection',
        });
      });
  };

  return (
    <Layout hideToolbar>
      <main className={styles['envelope-container']} data-testid={'envelope-container'}>
        <EnvelopePreview selectedEnvelope={selectedEnvelope} />
        <SelectEnvelopeCard
          register={register}
          handleBack={handleBack}
          handleContinue={onContinue}
          selectedEnvelope={selectedEnvelope}
        />
      </main>
    </Layout>
  );
};
